import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, gbp, downloadPdf, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Download, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const EMPTY_DRIVER = {
  name: "",
  driver_id: "",
  trading_name: "",
  uses_company_vehicle: false,
  vehicle_reg: "",
  bank_name: "",
  bank_account_name: "",
  bank_sort_code: "",
  bank_account_number: "",
};
const EMPTY_LINE = { description: "", quantity: 0, rate: 0, amount: 0 };

export default function SelfBillForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [subs, setSubs] = useState([]);
  const [subId, setSubId] = useState("");
  const [driver, setDriver] = useState(EMPTY_DRIVER);
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [payPeriodFrom, setPayPeriodFrom] = useState(today());
  const [payPeriodTo, setPayPeriodTo] = useState(today());
  const [location, setLocation] = useState("");
  const [totalDays, setTotalDays] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("Bank Transfer");
  const [dueDate, setDueDate] = useState(addDays(today(), 15));
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [number, setNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/contacts", { params: { type: "subcontractor" } }).then((r) => setSubs(r.data));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/self-bills/${id}`).then((r) => {
      const sb = r.data;
      setSubId(sb.subcontractor_id || "");
      setDriver({ ...EMPTY_DRIVER, ...(sb.driver_snapshot || {}) });
      setInvoiceDate((sb.invoice_date || "").slice(0, 10));
      setPayPeriodFrom((sb.pay_period_from || "").slice(0, 10));
      setPayPeriodTo((sb.pay_period_to || "").slice(0, 10));
      setLocation(sb.location || "");
      setTotalDays(sb.total_days_worked || 0);
      setPaymentMethod(sb.payment_method || "Bank Transfer");
      setDueDate((sb.due_date || "").slice(0, 10));
      setNotes(sb.notes || "");
      setLineItems(sb.line_items.length ? sb.line_items : [{ ...EMPTY_LINE }]);
      setNumber(sb.number);
    });
  }, [id, isEdit]);

  const onSubSelect = (val) => {
    if (val === "__manual") { setSubId(""); return; }
    setSubId(val);
    const s = subs.find((x) => x.id === val);
    if (s) {
      setDriver({
        name: s.name,
        driver_id: s.driver_id || "",
        trading_name: s.trading_name || "",
        uses_company_vehicle: !!s.uses_company_vehicle,
        vehicle_reg: s.vehicle_reg || "",
        bank_name: s.bank_name || "",
        bank_account_name: s.bank_account_name || "",
        bank_sort_code: s.bank_sort_code || "",
        bank_account_number: s.bank_account_number || "",
      });
    }
  };

  const updateLine = (i, patch) => {
    setLineItems((prev) => {
      const next = prev.map((li, idx) => (idx === i ? { ...li, ...patch } : li));
      const li = next[i];
      const qty = Number(li.quantity) || 0;
      const rate = Number(li.rate) || 0;
      if (patch.quantity !== undefined || patch.rate !== undefined) {
        if (qty && rate) next[i].amount = +(qty * rate).toFixed(2);
      }
      return next;
    });
  };

  const totals = useMemo(() => {
    const gross = lineItems.reduce((s, li) => s + Math.max(0, Number(li.amount) || 0), 0);
    const deductions = lineItems.reduce((s, li) => s + Math.min(0, Number(li.amount) || 0), 0); // negative number
    const total = gross + deductions;
    return {
      subtotal: +gross.toFixed(2),
      deductions: +Math.abs(deductions).toFixed(2),
      total: +total.toFixed(2),
    };
  }, [lineItems]);

  const submit = async () => {
    if (!driver.name) return toast.error("Please enter or select a driver.");
    if (lineItems.every((li) => !li.description && !Number(li.amount))) {
      return toast.error("Please add at least one line item.");
    }
    const payload = {
      subcontractor_id: subId || null,
      driver_snapshot: driver,
      invoice_date: invoiceDate,
      pay_period_from: payPeriodFrom,
      pay_period_to: payPeriodTo,
      location,
      total_days_worked: Number(totalDays) || 0,
      payment_method: paymentMethod,
      due_date: dueDate,
      notes,
      line_items: lineItems
        .filter((li) => li.description || Number(li.amount))
        .map((li) => ({
          description: li.description,
          quantity: Number(li.quantity) || 0,
          rate: Number(li.rate) || 0,
          amount: Number(li.amount) || 0,
        })),
    };
    setSaving(true);
    try {
      const res = isEdit
        ? await api.put(`/self-bills/${id}`, payload)
        : await api.post("/self-bills", payload);
      toast.success(`${res.data.number} saved`);
      navigate(`/self-bills/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDownload = async () => {
    if (!isEdit) return toast.info("Save the self-bill first to download a PDF.");
    try {
      await downloadPdf(`/self-bills/${id}/pdf`, `${number || "SelfBill"}.pdf`);
    } catch {
      toast.error("Could not download PDF");
    }
  };

  return (
    <div data-testid="sb-form-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <Link to="/self-bills" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Back to Self-Bills
          </Link>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">
            {isEdit ? `Self-Bill ${number}` : "New Self-Bill"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && (
            <Button data-testid="sb-download-btn" variant="outline" className="rounded-sm border-slate-300" onClick={onDownload}>
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          )}
          <Button data-testid="sb-save-btn" onClick={submit} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <Save className="h-4 w-4 mr-2" /> {isEdit ? "Update" : "Create"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-sm border border-slate-200 shadow-none p-6 lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Self-Employed Driver</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Driver (saved)</Label>
              <Select value={subId || "__manual"} onValueChange={onSubSelect}>
                <SelectTrigger data-testid="sb-driver-select" className="mt-1 rounded-sm border-slate-300">
                  <SelectValue placeholder="Choose a self-employed driver or type manually" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__manual">— Type manually —</SelectItem>
                  {subs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.driver_id ? ` (${s.driver_id})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field label="Driver name" testid="sb-driver-name" value={driver.name}
              onChange={(v) => setDriver({ ...driver, name: v })} />
            <Field label="Driver ID (e.g. D4PS-DR007)" testid="sb-driver-id" value={driver.driver_id}
              onChange={(v) => setDriver({ ...driver, driver_id: v })} />
            <Field label="Trading name" testid="sb-driver-trading" value={driver.trading_name}
              onChange={(v) => setDriver({ ...driver, trading_name: v })} />
            <Field label="Reference" testid="sb-location" value={location} onChange={setLocation} />
          </div>
        </Card>

        <Card className="rounded-sm border border-slate-200 shadow-none p-6">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Invoice Details</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Invoice date</Label>
              <Input data-testid="sb-invoice-date" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Period from</Label>
              <Input data-testid="sb-period-from" type="date" value={payPeriodFrom} onChange={(e) => setPayPeriodFrom(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Period to</Label>
              <Input data-testid="sb-period-to" type="date" value={payPeriodTo} onChange={(e) => setPayPeriodTo(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Total days</Label>
              <Input data-testid="sb-total-days" type="number" min="0" step="0.5" value={totalDays} onChange={(e) => setTotalDays(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Due date</Label>
              <Input data-testid="sb-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Payment method</Label>
              <Input data-testid="sb-payment-method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
            </div>
            {isEdit && (
              <div className="col-span-2 pt-2 border-t border-slate-100">
                <div className="text-xs text-slate-500">Reference</div>
                <div className="font-display text-xl font-semibold text-slate-900">{number}</div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Work summary lines */}
      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Work Summary</div>
          <Button data-testid="sb-add-line" size="sm" variant="outline" className="rounded-sm border-slate-300"
            onClick={() => setLineItems([...lineItems, { ...EMPTY_LINE }])}>
            <Plus className="h-4 w-4 mr-1" /> Add line
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full po-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-semibold">Description of Work</th>
                <th className="px-5 py-3 font-semibold text-right w-28">Qty / Days</th>
                <th className="px-5 py-3 font-semibold text-right w-32">Rate (£)</th>
                <th className="px-5 py-3 font-semibold text-right w-32">Amount (£)</th>
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-5 py-2">
                    <Input data-testid={`sb-line-desc-${i}`} value={li.description}
                      onChange={(e) => updateLine(i, { description: e.target.value })}
                      placeholder="e.g. Multidrop at NNL"
                      className="rounded-sm border-slate-300" />
                  </td>
                  <td className="px-5 py-2">
                    <Input data-testid={`sb-line-qty-${i}`} type="number" step="0.01" value={li.quantity}
                      onChange={(e) => updateLine(i, { quantity: e.target.value })}
                      className="rounded-sm border-slate-300 text-right" />
                  </td>
                  <td className="px-5 py-2">
                    <Input data-testid={`sb-line-rate-${i}`} type="number" step="0.01" value={li.rate}
                      onChange={(e) => updateLine(i, { rate: e.target.value })}
                      className="rounded-sm border-slate-300 text-right" />
                  </td>
                  <td className="px-5 py-2">
                    <Input data-testid={`sb-line-amount-${i}`} type="number" step="0.01" value={li.amount}
                      onChange={(e) => updateLine(i, { amount: Number(e.target.value) })}
                      className="rounded-sm border-slate-300 text-right" />
                  </td>
                  <td className="px-5 py-2 text-right">
                    <Button data-testid={`sb-line-remove-${i}`} variant="ghost" size="icon"
                      className="h-8 w-8 rounded-sm text-slate-500 hover:text-red-600"
                      onClick={() => setLineItems(lineItems.filter((_, idx) => idx !== i))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
          <div className="w-full max-w-xs space-y-2 text-sm" data-testid="sb-totals">
            <div className="flex justify-between text-slate-700">
              <span>Subtotal</span><span className="tabular-nums">{gbp(totals.subtotal)}</span>
            </div>
            {totals.deductions > 0 && (
              <div className="flex justify-between text-red-600" data-testid="sb-deductions">
                <span>Deductions</span><span className="tabular-nums">−{gbp(totals.deductions)}</span>
              </div>
            )}
            <div className="border-t border-slate-200 pt-2 flex justify-between font-display font-semibold text-base text-slate-900">
              <span>TOTAL PAYABLE</span><span className="tabular-nums">{gbp(totals.total)}</span>
            </div>
            <div className="text-xs text-slate-400 pt-1">No VAT applied on self-bills. Negative-amount lines are treated as deductions.</div>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border border-slate-200 shadow-none p-6">
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Additional notes</Label>
        <Textarea data-testid="sb-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
          className="mt-3 rounded-sm border-slate-300" />
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm border-slate-300" />
    </div>
  );
}
